import React from 'react';
import SEO from '../components/SEO';
import { homeFaqJsonLd } from '../data/homeFaqs';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Globe, Award, Users, CheckCircle, Stethoscope, HeartPulse,
  Briefcase, MapPin, Star, TrendingUp, Shield, BadgeCheck, Building2,
  PawPrint, Smile, Brain, Sparkles, Quote, Phone, MessageCircle,
} from 'lucide-react';
import { testimonialsData } from '../mock';
import { programs } from '../data/programsData';
import { ALL_STUDY_DESTINATIONS } from '../data/studyDestinationConfigs';
import FAQ from '../components/FAQ';
import VideoReel from '../components/VideoReel';
import LiveActivityTicker from '../components/LiveActivityTicker';
import CountryLeadForm from '../components/CountryLeadForm';
import { liveActivity } from '../data/successStories';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { useEligibilityModal } from '../context/EligibilityModalContext';

const featuredIconMap = {
  Stethoscope, HeartPulse, PawPrint, Smile, Brain, Shield, Briefcase, TrendingUp,
};

// Study destinations shown on Home — kept in-file so this page stays self-contained.
const homeDestinations = [
  { slug: 'georgia',  name: 'Georgia',        flag: '🇬🇪', tagline: 'NMC-approved MBBS & Nursing hub',                     highlights: ['MBBS', 'BSc Nursing'],           stat: '6+ Universities',        href: '/study-in-georgia',  gradientFrom: '#8B2E2E', gradientTo: '#4A1414' },
  { slug: 'bulgaria', name: 'Bulgaria',       flag: '🇧🇬', tagline: 'EU-accredited medical & nursing programs',            highlights: ['MBBS', 'Nursing', 'Vet'],         stat: '4+ Universities',        href: '/study-in-bulgaria', gradientFrom: '#0B7A5A', gradientTo: '#053B2C' },
  { slug: 'moldova',  name: 'Moldova',        flag: '🇲🇩', tagline: 'Affordable EU medical education',                     highlights: ['MBBS', 'Vet'],                    stat: 'Nicolae Testemițanu',    href: '/study-in-moldova',  gradientFrom: '#0A4C8A', gradientTo: '#052748' },
  { slug: 'germany',  name: 'Germany',        flag: '🇩🇪', tagline: 'Ausbildung & tuition-free public universities',       highlights: ['Ausbildung', "Bachelor's", "Master's"], stat: 'Public + Private', href: '/study-in-germany',  gradientFrom: '#1F2937', gradientTo: '#0B1220' },
  { slug: 'france',   name: 'France',         flag: '🇫🇷', tagline: 'World-class engineering, business & design',          highlights: ["Bachelor's", "Master's", 'MBA'],  stat: 'Public + Grande Écoles', href: '/study-in-france',   gradientFrom: '#2B4CB8', gradientTo: '#0F1E5C' },
  { slug: 'uk',       name: 'United Kingdom', flag: '🇬🇧', tagline: 'Top-ranked English-medium universities',              highlights: ["Bachelor's", "Master's", 'MBA'],  stat: 'Russell Group & More',   href: '/study-in-uk',       gradientFrom: '#1E3A8A', gradientTo: '#0B1E4F' },
  { slug: 'austria',  name: 'Austria',        flag: '🇦🇹', tagline: 'Affordable public universities in the heart of Europe', highlights: ["Bachelor's", "Master's", 'PhD'], stat: 'Low Tuition · High Quality', href: '/study-in-austria', gradientFrom: '#B22234', gradientTo: '#5A0F1A' },
  { slug: 'malta',    name: 'Malta',          flag: '🇲🇹', tagline: 'English-medium EU with strong nursing pathway',       highlights: ['BSc Nursing', 'Business', 'IT'],  stat: 'EU-Accredited · English', href: '/study-in-malta',   gradientFrom: '#0057B7', gradientTo: '#002B5C' },
];

const Home = () => {
  const { open: openEligibility } = useEligibilityModal();
  const triggerModal = (src) => openEligibility(src);

  return (
    <div className="min-h-screen">
      <SEO canonical="/" jsonLd={homeFaqJsonLd} />
      {/* ═══════════════════════════════════════════════════════════
          1. HERO — cinematic, tighter, single-purpose
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[calc(100vh-64px)] sm:min-h-[85vh] md:min-h-[88vh] flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="/img/5049izd6_img_7600.webp"
            srcSet="/img/5049izd6_img_7600-800.webp 800w, /img/5049izd6_img_7600.webp 1400w"
            sizes="100vw"
            fetchpriority="high"
            decoding="async"
            alt="Patrick's Edu Care students with Dr. Patrick at their university abroad"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545]/95 via-[#0B2545]/75 to-[#0B2545]/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2545]/40 via-transparent to-[#0B2545]/60" />
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}
            aria-hidden="true"
          />
          <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-[#4DD4AC]/15 blur-3xl" aria-hidden="true" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#2B5B8A]/20 blur-3xl" aria-hidden="true" />
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-20 sm:pt-24 md:pt-28 pb-10 sm:pb-14 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-4 sm:mb-5">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#4DD4AC] animate-pulse" />
              <span className="text-[10px] sm:text-xs font-medium tracking-wide uppercase">
                ISO 9001:2015 · 15+ Years of Excellence
              </span>
            </div>

            <h1 className="text-[34px] sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-3 sm:mb-5 leading-[1.05]">
              Your Global Future <br />
              <span className="text-[#4DD4AC]">
                Starts Here.
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-white/85 mb-5 sm:mb-7 max-w-2xl leading-relaxed">
              End-to-end guidance for Indian students choosing MBBS, Nursing, Ausbildung,
              Engineering, Business &amp; more — across 8 world-class destinations.
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 mb-4 sm:mb-6">
              <button
                onClick={() => triggerModal('home')}
                data-testid="hero-eligibility-btn"
                className="group px-6 py-3 sm:px-7 sm:py-3.5 rounded-full font-semibold text-[#0B2545] bg-[#4DD4AC] hover:bg-[#3DBFA0] hover:shadow-[0_10px_40px_rgba(77,212,172,0.4)] hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                Check Your Eligibility
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#destinations"
                data-testid="hero-destinations-btn"
                className="px-6 py-3 sm:px-7 sm:py-3.5 rounded-full font-semibold text-white border border-white/30 bg-white/5 backdrop-blur-md hover:bg-white/15 hover:border-white/50 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                Explore Destinations
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 sm:gap-x-5 sm:gap-y-2 text-[11px] sm:text-xs text-white/75 pt-4 sm:pt-5 border-t border-white/15">
              <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> NMC &amp; WHO Approved</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> Direct University Tie-ups</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> No Donation</span>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs hidden md:flex items-center gap-2 animate-bounce" aria-hidden="true">
          <div className="w-5 h-8 rounded-full border border-white/40 flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          2. LIVE ACTIVITY TICKER (thin social-proof strip)
      ═══════════════════════════════════════════════════════════ */}
      <LiveActivityTicker items={liveActivity} />

      {/* ═══════════════════════════════════════════════════════════
          3. TRUST BAND — Stats + ISO combined, single elegant strip
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-8 md:py-12 bg-white border-b border-gray-100 relative" data-testid="home-trust-band">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 md:divide-x md:divide-gray-200">
            {[
              { icon: Users,      label: 'Students Placed',        value: 2500, suffix: '+', color: '#2B5B8A' },
              { icon: Building2,  label: 'Direct University Ties', value: 20,   suffix: '+', color: '#4DD4AC' },
              { icon: Globe,      label: 'Study Destinations',     value: 15,   suffix: '+', color: '#7B61FF' },
              { icon: TrendingUp, label: 'Visa Success Rate',      value: 98,   suffix: '%', color: '#06A77D' },
            ].map((stat, index) => {
              const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  ref={ref}
                  initial={{ opacity: 0, y: 14 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className="flex items-center justify-center gap-3 md:gap-4 px-2"
                >
                  <div
                    className="w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${stat.color}15` }}
                  >
                    <Icon size={20} style={{ color: stat.color }} />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl md:text-3xl font-bold text-[#1A2B3C] leading-none">
                      {inView && <CountUp end={stat.value} duration={2} suffix={stat.suffix} />}
                    </div>
                    <div className="text-[11px] md:text-xs text-[#4A5B6C] font-medium mt-1">{stat.label}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          4. STUDY DESTINATIONS — 8 country cards
      ═══════════════════════════════════════════════════════════ */}
      <section
        id="destinations"
        className="py-10 md:py-16 bg-[#F8FAFC] relative overflow-hidden"
        data-testid="home-destinations"
      >
        <div className="absolute top-24 -left-24 w-96 h-96 rounded-full bg-[#2B5B8A]/5 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-24 -right-24 w-96 h-96 rounded-full bg-[#4DD4AC]/5 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-[#2B5B8A]/10 border border-[#2B5B8A]/20 text-[#2B5B8A] text-[11px] sm:text-xs font-semibold mb-2.5 sm:mb-4">
              <Globe size={12} /> Our Study Destinations
            </div>
            <h2 className="text-[26px] sm:text-4xl md:text-5xl font-bold text-[#1A2B3C] mb-2 sm:mb-3 leading-[1.1]">
              Where We <span className="text-[#2B5B8A]">Send Students</span>
            </h2>
            <p className="text-[13px] sm:text-base text-[#4A5B6C]">
              8 carefully-curated destinations, one dedicated Patrick&apos;s team — end-to-end guidance from application to first day on campus.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 max-w-6xl mx-auto">
            {homeDestinations.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
              >
                <Link
                  to={d.href}
                  data-testid={`home-destination-${d.slug}`}
                  className="group relative rounded-2xl overflow-hidden bg-white border border-gray-100 hover:border-[#4DD4AC]/40 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all block h-full"
                >
                  <div
                    className="relative h-28 md:h-32 overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${d.gradientFrom} 0%, ${d.gradientTo} 100%)` }}
                  >
                    <div
                      className="absolute inset-0 opacity-15"
                      style={{
                        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                        backgroundSize: '22px 22px',
                      }}
                      aria-hidden="true"
                    />
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl group-hover:bg-white/20 transition-all duration-500" aria-hidden="true" />
                    <div className="absolute top-2.5 md:top-3 left-3 text-3xl md:text-4xl leading-none drop-shadow-lg group-hover:scale-110 transition-transform duration-500">
                      {d.flag}
                    </div>
                    <div className="absolute bottom-2.5 left-3 right-3">
                      <h3
                        className="text-white font-extrabold text-base md:text-lg leading-tight drop-shadow"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {d.name}
                      </h3>
                      <p className="text-white/85 text-[10px] md:text-[11px] font-medium mt-0.5 line-clamp-1">
                        {d.tagline}
                      </p>
                    </div>
                  </div>

                  <div className="p-3 md:p-3.5">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {d.highlights.slice(0, 3).map((h) => (
                        <span
                          key={h}
                          className="text-[9px] md:text-[10px] font-bold px-1.5 md:px-2 py-0.5 rounded-full bg-[#F0F8FF] text-[#2B5B8A] border border-[#2B5B8A]/10"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-[#4A5B6C] font-medium truncate">
                        {d.stat}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-xs md:text-sm font-bold text-[#2B5B8A] group-hover:text-[#4DD4AC] transition-colors flex-shrink-0">
                        <span className="hidden md:inline">Explore</span>
                        <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-6 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => triggerModal('home:destinations')}
              data-testid="home-destinations-cta"
              className="btn-primary inline-flex items-center justify-center"
            >
              Not sure where to study? Talk to a counsellor <ArrowRight className="ml-2" size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          5. PROGRAMS — 8 program cards, condensed
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-10 md:py-16 bg-white" data-testid="home-programs">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-[#4DD4AC]/10 border border-[#4DD4AC]/30 text-[#0B7A5A] text-[11px] sm:text-xs font-semibold mb-2.5 sm:mb-4">
              <Sparkles size={12} /> Our Main Programs
            </div>
            <h2 className="text-[26px] sm:text-4xl md:text-5xl font-bold text-[#1A2B3C] mb-2 sm:mb-3 leading-[1.1]">
              Choose Your <span className="text-[#2B5B8A]">Path</span>
            </h2>
            <p className="text-[13px] sm:text-base text-[#4A5B6C]">
              Click any program to see the countries and universities we represent — with tuition, eligibility &amp; intake details.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 max-w-6xl mx-auto">
            {programs.map((p, i) => {
              const Icon = featuredIconMap[p.icon] || Briefcase;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.3, delay: i * 0.03 }}
                >
                  <Link
                    to={`/programs/${p.slug}`}
                    data-testid={`home-program-${p.slug}`}
                    className="group bg-[#F8FAFC] p-4 md:p-5 rounded-2xl border border-[#2B5B8A]/10 hover:border-[#4DD4AC]/40 hover:shadow-xl hover:-translate-y-1 transition-all block relative overflow-hidden h-full"
                  >
                    <div
                      className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 -translate-y-10 translate-x-10 group-hover:scale-150 transition-transform duration-500"
                      style={{ backgroundColor: p.color }}
                      aria-hidden="true"
                    />
                    <div
                      className="w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center mb-3 relative z-10"
                      style={{ backgroundColor: `${p.color}15` }}
                    >
                      <Icon size={22} style={{ color: p.color }} />
                    </div>
                    <h3 className="font-bold text-[#1A2B3C] text-sm md:text-base mb-1">{p.name}</h3>
                    <p className="text-[11px] md:text-xs text-[#4A5B6C] mb-2.5 line-clamp-2">{p.tagline}</p>
                    <div className="flex items-center justify-between text-[11px] text-[#4A5B6C]">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} className="text-[#4DD4AC]" />
                        {p.countries.length} {p.countries.length === 1 ? 'country' : 'countries'}
                      </span>
                      <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[#2B5B8A] group-hover:text-[#4DD4AC] transition-colors">
                        <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-6 sm:mt-10">
            <Link to="/programs" className="btn-secondary inline-flex items-center justify-center" data-testid="home-programs-view-all">
              View All Programs <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          6. WHY PATRICK'S — 3 differentiator cards
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-10 md:py-16 bg-white relative overflow-hidden" data-testid="home-why-choose">
        <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-[#4DD4AC]/5 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-6 sm:mb-10 md:mb-12 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-[#2B5B8A]/10 text-[#2B5B8A] text-[11px] sm:text-xs font-semibold mb-2.5 sm:mb-4">
              <Sparkles size={12} /> Why Patrick&apos;s Edu Care
            </div>
            <h2 className="text-[26px] sm:text-4xl md:text-5xl font-bold text-[#1A2B3C] mb-2 sm:mb-3 leading-[1.1]">
              Your Career, in <span className="text-[#2B5B8A]">Safe Hands</span>
            </h2>
            <p className="text-[13px] sm:text-base text-[#4A5B6C]">
              15+ years guiding Indian students into world-class universities — with transparency at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
            {[
              { icon: Award,      title: 'Dr. Patrick, personally',   desc: 'Talk 1-on-1 with the founder — no junior counsellors, no scripted answers.',       color: '#2B5B8A', stat: 'Founder-led' },
              { icon: Shield,     title: 'Direct university ties',    desc: 'Direct partnerships with NMC, WHO &amp; EU recognised universities. No middlemen.', color: '#4DD4AC', stat: '20+ universities' },
              { icon: TrendingUp, title: 'End-to-end, even abroad',   desc: 'From counselling to visa to settling in — even after you land, we stay with you.', color: '#7B61FF', stat: '24/7 support' },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group relative bg-white rounded-2xl p-5 md:p-6 border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden"
                data-testid={`why-choose-card-${i}`}
              >
                <div className="absolute top-0 left-0 right-0 h-1 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ backgroundColor: card.color }} />
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10 group-hover:scale-125 transition-transform duration-500" style={{ backgroundColor: card.color }} aria-hidden="true" />
                <div className="flex items-start justify-between mb-3 relative z-10">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                    <card.icon size={22} style={{ color: card.color }} />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                    {card.stat}
                  </span>
                </div>
                <h3 className="font-bold text-[#1A2B3C] text-lg mb-1.5 relative z-10" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {card.title}
                </h3>
                <p className="text-sm text-[#4A5B6C] leading-relaxed relative z-10" dangerouslySetInnerHTML={{ __html: card.desc }} />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-6 sm:mt-10">
            <Link to="/services" data-testid="home-services-link" className="inline-flex items-center gap-2 text-sm font-bold text-[#2B5B8A] hover:text-[#4DD4AC] transition-colors">
              See all 8 support services we provide <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          7. VIDEO REEL — real moments (compact)
      ═══════════════════════════════════════════════════════════ */}
      <VideoReel />

      {/* ═══════════════════════════════════════════════════════════
          8. TESTIMONIALS — premium 3-col
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-10 md:py-16 bg-[#F8FAFC] relative overflow-hidden" data-testid="home-testimonials">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #2B5B8A 1px, transparent 1px)', backgroundSize: '40px 40px' }} aria-hidden="true" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFC107]/15 text-[#B26A00] text-[11px] sm:text-xs font-semibold mb-2.5 sm:mb-4">
              <Star size={12} className="fill-[#B26A00]" /> 4.9 / 5 from 500+ students &amp; parents
            </div>
            <h2 className="text-[26px] sm:text-4xl md:text-5xl font-bold text-[#1A2B3C] mb-2 sm:mb-3 leading-[1.1]">
              Real Students. <span className="text-[#2B5B8A]">Real Journeys.</span>
            </h2>
            <p className="text-[13px] sm:text-base text-[#4A5B6C]">
              Hear from students now living their global dreams — in their own words.
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-5 flex md:overflow-visible overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            {testimonialsData.slice(0, 3).map((t, index) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="snap-center flex-shrink-0 w-[85%] md:w-auto bg-white p-6 rounded-3xl border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 relative group"
                data-testid={`home-testimonial-${index}`}
              >
                <Quote size={36} className="absolute top-5 right-5 text-[#2B5B8A]/10" aria-hidden="true" />

                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#FFC107] text-[#FFC107]" />
                  ))}
                </div>

                <p className="text-sm md:text-[15px] text-[#1A2B3C] mb-5 leading-relaxed line-clamp-5 relative z-10" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  &ldquo;{t.testimonial}&rdquo;
                </p>

                <div className="flex items-center pt-4 border-t border-gray-100">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover mr-3 ring-2 ring-[#4DD4AC]/40"
                  />
                  <div className="min-w-0">
                    <h4 className="font-bold text-[#1A2B3C] text-sm truncate">{t.name}</h4>
                    <p className="text-[11px] text-[#4A5B6C] truncate">{t.course}</p>
                    <p className="text-[10px] text-[#2B5B8A] font-semibold mt-0.5 truncate">{t.university}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-6 sm:mt-10">
            <Link to="/success-stories" data-testid="home-testimonials-view-all" className="inline-flex items-center gap-2 text-sm font-bold text-[#2B5B8A] hover:text-[#4DD4AC] transition-colors">
              Read all success stories <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          9. FAQ
      ═══════════════════════════════════════════════════════════ */}
      <FAQ />

      {/* ═══════════════════════════════════════════════════════════
          10. FINAL CTA — split with inline lead form (like country pages)
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative py-10 md:py-16 overflow-hidden text-white" data-testid="home-final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2545] via-[#13315C] to-[#2B5B8A]" aria-hidden="true" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} aria-hidden="true" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#4DD4AC]/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#2B5B8A]/30 blur-3xl" aria-hidden="true" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
            {/* LEFT — Pitch */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-xs font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-[#4DD4AC] animate-pulse" />
                Free 1-on-1 counselling · No obligation
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-[42px] font-bold mb-4 leading-[1.08]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Ready to begin <br className="sm:hidden" />
                <span className="text-[#4DD4AC]">
                  your global story?
                </span>
              </h2>
              <p className="text-white/85 mb-6 text-base md:text-lg max-w-lg leading-relaxed">
                Talk directly with Dr. Patrick&apos;s team. We&apos;ll match you to the right program, country and university — at zero cost.
              </p>

              <ul className="space-y-2.5 mb-6">
                {[
                  'Direct university partnerships — no middlemen',
                  'End-to-end guidance: application, visa &amp; pre-departure',
                  'Post-arrival support, even after you land abroad',
                ].map((l) => (
                  <li key={l} className="flex items-start gap-2.5 text-sm text-white/90">
                    <CheckCircle size={16} className="text-[#4DD4AC] mt-0.5 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: l }} />
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="tel:+917594881898"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 text-white font-bold text-sm transition"
                  data-testid="home-cta-phone"
                >
                  <Phone size={14} /> +91 75948 81898
                </a>
                <a
                  href="https://wa.me/917558881898"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#20B858] text-white font-bold text-sm transition"
                  data-testid="home-cta-whatsapp"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>

              <div className="mt-6 pt-5 border-t border-white/15 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/70">
                <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> ISO 9001:2015</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> 2,500+ Placed</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-[#4DD4AC]" /> 98% Visa Success</span>
              </div>
            </div>

            {/* RIGHT — Inline lead form with country selector */}
            <div>
              <CountryLeadForm
                variant="cta"
                source="patricks-website:home:final-cta-inline"
                showCountrySelector
                countryOptions={ALL_STUDY_DESTINATIONS}
                accent="#4DD4AC"
                primary="#2B5B8A"
                dark="#0B2545"
                headline="Get Your Free Profile Assessment"
                subheadline="A senior counsellor will call you within 24 hours with a personalised plan — free, no obligation."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
